import { FC, JSX, Ref, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { startCase } from 'lodash';
import classNames from 'classnames';
import {
  MatchQuery,
  RangeQuery,
} from '@opensearch-project/opensearch/api/_types/_common.query_dsl';

import { useSpecialsContext } from '@/context';
import {
  getCSSpropety,
  getPreCartTreezDiscounts,
  normalizeSizeLabel,
} from '@/utils';
import {
  useNumericMenu,
  useOpenSearchContext,
  useRefinementList,
  useRefinementsHelpers,
  useSortingMenu,
  useUpdateSearchParams,
} from '@/opensearch';
import {
  FilterAttributeNameMapping,
  PRODUCT_FILTER_ATTRIBUTES,
} from '@/constants';
import { useDebounced } from '@/hooks';
import { OnSaleQueryFilter } from 'opensearch/constants';

import { RefinementList, RefinementListItem } from './RefinementList';
import { FiltersModal } from './FiltersModal';
import { Container } from '../Container';
import { IconSvg } from '../IconSvg';
import { Button } from '../Button';
import { Accordion } from '../Accordion';
import { Select } from '../Select';
import { Icon } from '../atoms/icon';
import { RangeSlider } from '../RangeSlider';
import {
  filterAttributeNameMapping,
  filtersOrder,
  filterTitleMapping,
  sortingOptions,
} from './data';

import styles from './FiltersTreez.module.scss';

const FiltersContainer = ({ children }) => {
  const [top, setTop] = useState<number | null>(null);
  const filtersTop = getCSSpropety('top', top);

  useEffect(() => {
    const handleScroll = () => {
      const header = document.getElementsByTagName('header')[0];
      if (!header) return;

      const headerBottom = header.getBoundingClientRect().bottom;
      setTop(headerBottom);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Container className={styles.section} style={filtersTop}>
      {children}
    </Container>
  );
};

interface IFiltersList {
  className: string;
  showDealsFilter?: boolean;
}

const FiltersList: FC<IFiltersList> = ({ className, showDealsFilter }) => {
  const updatedFiltersOrder = filtersOrder.filter(name =>
    !showDealsFilter ? name !== 'deals' : true,
  );

  return (
    <>
      <ActiveFiltersSection />
      <div className={(styles.filters_list__container, className)}>
        <div className={classNames(styles.filters_list)}>
          {updatedFiltersOrder.map(filter => {
            const Filter = filterComponents?.[filter];

            if (!Filter) return null;

            return <Filter key={filter} />;
          })}
        </div>
      </div>
    </>
  );
};

const extraFilters: IOutsideFilter[] = [
  {
    name: 'deals',
    attribute: PRODUCT_FILTER_ATTRIBUTES._ON_SALE,
    icon: 'gift',
    type: 'special',
    id: 'active',
  },
  {
    name: 'category',
    attribute: PRODUCT_FILTER_ATTRIBUTES._CATEGORY,
    icon: 'leaf',
  },
  {
    name: 'classification',
    attribute: PRODUCT_FILTER_ATTRIBUTES._CLASSIFICATION,
    icon: 'drop',
  },
  {
    name: 'brand',
    attribute: PRODUCT_FILTER_ATTRIBUTES._BRAND,
  },
  {
    name: 'subType',
    attribute: PRODUCT_FILTER_ATTRIBUTES._SUBTYPE,
  },
];

const activesExtraFilters = filtersOrder
  .map(name => extraFilters.find(filter => filter.name === name))
  .filter(Boolean) as IOutsideFilter[];

const OutsideFilters = () => (
  <div className={styles.slider_container}>
    {activesExtraFilters?.map(({ name, attribute, icon, type, id }) => (
      <OutsideFilter
        attribute={attribute}
        icon={icon}
        id={id}
        key={`outside-filter-${name}`}
        name={name}
        type={type}
      />
    ))}
  </div>
);

interface IOutsideFilter {
  name: 'deals' | 'category' | 'classification' | 'brand' | 'subType';
  attribute: string;
  icon?: string;
  type?: 'button' | 'dropdown' | 'special';
  id?: string;
}

const OutsideFilter: FC<IOutsideFilter> = ({
  name,
  attribute,
  icon,
  type = 'dropdown',
  id: _,
}) => {
  const [button, setButton] = useState<HTMLButtonElement | undefined>();
  const [panel, setPanel] = useState(null);
  const pathname = usePathname();
  const { clearAll } = useRefinementList({ attribute });
  const { activeFilters } = useRefinementsHelpers();
  const count = activeFilters?.[attribute]?.items?.length || 0;
  const { items } = useRefinementList({ attribute });

  const isDropdownType = type === 'dropdown';

  const { styles: popperStyles, attributes } = usePopper(button, panel, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 16,
        },
      },
    ],
    placement: 'bottom-start',
  });

  const isHidden =
    (name === 'brand' && pathname?.includes('brand')) ||
    (name === 'category' && pathname?.includes('collection')) ||
    (name === 'subType' && pathname?.includes('subcategory')) ||
    (name === 'classification' && pathname?.includes('strain')) ||
    (name === 'deals' &&
      (pathname?.includes('deals') || pathname?.includes('special')));

  if (isHidden || !items.length) return null;

  if (type === 'special') {
    return <SpecialFilter attribute={attribute} icon={icon} />;
  }

  return (
    <Popover className={styles.section__filter_group_select}>
      {({ open }) => (
        <>
          <PopoverButton
            aria-controls={`filter-panel-id-${filterTitleMapping[name]}`}
            aria-expanded={open}
            aria-label={`Filter by ${filterTitleMapping[name]}`}
            className={classNames(styles.section__select_button, styles.item, {
              [styles.section__select_button_open]: open,
              [styles.section__select_button_active]: !!count,
            })}
            ref={setButton as Ref<HTMLButtonElement>}
            role="button"
            tabIndex={0}
          >
            <div className={styles.section__select_button_label}>
              {icon && <IconSvg aria-hidden="true" name={icon} />}
              {filterTitleMapping[name]}
              {!!count && <span className={styles.filters_count}>{count}</span>}
            </div>
            {isDropdownType && (
              <IconSvg aria-hidden="true" name="chevron-right" />
            )}
          </PopoverButton>
          <PopoverPanel
            className={styles.section__select_panel}
            id={`filter-panel-id-${filterTitleMapping[name]}`}
            ref={setPanel as Ref<HTMLDivElement>}
            style={popperStyles.popper}
            {...attributes.popper}
          >
            <SingleFilter filter={name} />
            <div className={styles.button_row}>
              <Button
                className={styles.panel_button}
                color="secondary"
                onClick={clearAll}
              >
                <IconSvg name="bin" />
                <span>Clear</span>
              </Button>
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

const SpecialFilter: FC<{
  attribute: string;
  icon?: string;
}> = ({ attribute, icon }) => {
  const { updateSearchParams } = useUpdateSearchParams();
  const { queryFiltersByFacets, updateQueryFiltersByFacets } =
    useOpenSearchContext();
  const isActive = !!queryFiltersByFacets?.[attribute];

  const refine = () => {
    if (isActive) {
      updateSearchParams({ [attribute]: null });
    } else {
      updateSearchParams({ [attribute]: ['true'], page: '1' });
    }
    updateQueryFiltersByFacets(prev => {
      if (isActive) {
        const { [attribute]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [attribute]: OnSaleQueryFilter,
      };
    });
  };

  return (
    <div className={styles.section__filter_group_select}>
      <Button
        aria-label={`Filter by ${filterTitleMapping.deals}`}
        className={classNames(styles.section__select_button, styles.item, {
          [styles.section__select_button_active]: isActive,
        })}
        onClick={refine}
        size="md"
      >
        {icon && <IconSvg name={icon} />}
        {filterTitleMapping.deals}
        {isActive && <span className={styles.filters_count}>1</span>}
      </Button>
    </div>
  );
};

interface ISingleFilter {
  className?: string;
  filter:
    | 'brand'
    | 'category'
    | 'classification'
    | 'deals'
    | 'price'
    | 'productTypeName'
    | 'subType'
    | 'thc'
    | 'size';
}

const SingleFilter: FC<ISingleFilter> = ({ className, filter }) => {
  const Filter = filterComponents[filter];

  if (!Filter) return null;

  return (
    <div className={classNames(styles.filters_list__container, className)}>
      <div className={classNames(styles.filters_list)}>
        <Filter hasScrollBar key={filter} />
      </div>
    </div>
  );
};

const FilterButton = ({ onClick }) => {
  const { activeFiltersCount } = useRefinementsHelpers();

  return (
    <button
      className={classNames(styles.filter_menu__button, {
        [styles.filter_menu__button_active]: !!activeFiltersCount,
      })}
      onClick={onClick}
    >
      <IconSvg name="simple-filter" />
      <span className={styles.title}>
        FILTER
        {!!activeFiltersCount && (
          <span
            className={styles.filters_count}
          >{`${activeFiltersCount}`}</span>
        )}
      </span>
    </button>
  );
};

const ActiveFiltersSection: FC = () => {
  const { activeFilters: filters, activeFiltersCount } =
    useRefinementsHelpers();

  if (!activeFiltersCount) return null;

  return (
    <div className={styles.active_filters}>
      <div className={styles.active_filters_container}>
        {Object.entries(filters).map(([attribute, { items, type }]) => {
          if (type === 'refinement') {
            return (
              <RefinementActiveFilters
                attribute={attribute}
                items={items as MatchQuery[]}
                key={attribute}
              />
            );
          }
          return (
            <NumericActiveFilters
              attribute={attribute}
              items={items as RangeQuery[]}
              key={attribute}
            />
          );
        })}
      </div>
    </div>
  );
};

const ActiveFilterItem = ({
  filterName,
  onClick,
}: {
  filterName: string;
  onClick: () => void;
}) => (
  <Button className={styles.filter_pill} key={filterName} onClick={onClick}>
    {filterName}
    <span className={styles.pill_icon}>
      <Icon name="close" />
    </span>
  </Button>
);

type RefinementActiveFiltersProps = {
  attribute: string;
  items?: MatchQuery[];
};

const RefinementActiveFilters: FC<RefinementActiveFiltersProps> = ({
  attribute,
  items,
}) => {
  const { updateSearchParams } = useUpdateSearchParams();
  const { updateQueryFiltersByFacets } = useOpenSearchContext();
  const { refine } = useRefinementList({ attribute });
  const filterName = filterAttributeNameMapping?.[attribute] || attribute;
  const filterTitle = filterTitleMapping?.[filterName];

  const handleClick = (item: MatchQuery) => {
    if (attribute === PRODUCT_FILTER_ATTRIBUTES._ON_SALE) {
      updateSearchParams({ [attribute]: null });
      updateQueryFiltersByFacets(prev => {
        const { [attribute]: _, ...rest } = prev;
        return rest;
      });
      return;
    }
    refine(item as string | number | boolean);
  };

  return (
    <div className={styles.filter_container} key={filterTitle}>
      <p className={styles.filter_title}>{filterTitle}</p>
      <div className={styles.filter_list}>
        {items?.map((item, index) => {
          const label = item === true ? 'Active' : item?.toString() || '';
          const displayLabel =
            attribute === 'customSize' ? normalizeSizeLabel(label) : label;
          return (
            <ActiveFilterItem
              filterName={displayLabel}
              key={index}
              onClick={() => handleClick(item)}
            />
          );
        })}
      </div>
    </div>
  );
};

const formatAsPrice = (value: string | number): string => `$${value}`;

const formatAsPercentage = (value: string | number): string => `${value}%`;

type NumericActiveFiltersProps = {
  attribute: string;
  items?: RangeQuery[];
};

const NumericActiveFilters: FC<NumericActiveFiltersProps> = ({
  attribute,
  items,
}) => {
  const { refine } = useNumericMenu({ attribute, items: [] });
  const filterName = filterAttributeNameMapping?.[attribute] || attribute;
  const filterTitle = filterTitleMapping?.[filterName];

  const formatter = [
    PRODUCT_FILTER_ATTRIBUTES._TOTAL_TAC_PERCENT,
    PRODUCT_FILTER_ATTRIBUTES._TOTAL_THC_PERCENT,
    PRODUCT_FILTER_ATTRIBUTES._TOTAL_CBD_PERCENT,
  ].includes(attribute as PRODUCT_FILTER_ATTRIBUTES)
    ? formatAsPercentage
    : formatAsPrice;

  return (
    <div className={styles.filter_container} key={filterTitle}>
      <p className={styles.filter_title}>{filterTitle}</p>
      <div className={styles.filter_list}>
        {items?.map((item, index) => {
          const label =
            item.gte !== undefined &&
            item.lte !== undefined &&
            `${formatter(item.gte)} - ${formatter(item.lte)}`;

          return (
            <ActiveFilterItem
              filterName={label || ''}
              key={index}
              onClick={() => refine({})}
            />
          );
        })}
      </div>
    </div>
  );
};

const BrandFilter = ({ ...props }) => {
  const pathname = usePathname();

  if (pathname?.includes('brand')) return null;

  return (
    <RefinementSection
      attribute="brand.keyword"
      className={styles.refinement_list}
      hasSearchBar
      searchBarPlaceholder="Select brands"
      {...props}
    />
  );
};

const CategoryFilter = ({ ...props }) => {
  const pathname = usePathname();

  if (pathname?.includes('collection')) return null;

  return (
    <RefinementSection
      attribute="category.keyword"
      className={styles.refinement_list}
      {...props}
    />
  );
};

const DealsFilter = () => {
  const { specials } = useSpecialsContext();
  const { updateSearchParams } = useUpdateSearchParams();
  const { queryFiltersByFacets, updateQueryFiltersByFacets } =
    useOpenSearchContext();

  const attribute = PRODUCT_FILTER_ATTRIBUTES._ON_SALE;
  const isActive = !!queryFiltersByFacets?.[attribute];

  const refine = () => {
    if (isActive) {
      updateSearchParams({ [attribute]: null });
    } else {
      updateSearchParams({ [attribute]: ['true'], page: '1' });
    }
    updateQueryFiltersByFacets(prev => {
      if (isActive) {
        const { [attribute]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [attribute]: OnSaleQueryFilter,
      };
    });
  };

  const preCartTreezDiscounts = getPreCartTreezDiscounts(specials);

  if (!preCartTreezDiscounts?.length) return null;

  return (
    <Accordion
      classNameHeader={styles.filters__header_section}
      isExpanded
      label={<span>{filterTitleMapping?.deals ?? 'Deals'}</span>}
    >
      <RefinementListItem
        handleChange={refine}
        isChecked={isActive}
        label="Active"
        value="true"
      />
    </Accordion>
  );
};

const SubtypeFilter = ({ ...props }) => {
  const pathname = usePathname();
  const pathSegments = pathname?.split('/').filter(Boolean);

  // If the path is /collection/[slug]/[subcategory], we don't show the subtype filter
  if (pathSegments?.includes('collection') && pathSegments?.length === 3) {
    return null;
  }

  return (
    <RefinementSection
      attribute="subtype"
      className={styles.refinement_list}
      hasSearchBar
      searchBarPlaceholder="Select subtypes"
      {...props}
    />
  );
};

const ClassificationFilter = ({ ...props }) => {
  const pathname = usePathname();

  if (pathname?.includes('strain')) return null;

  return (
    <RefinementSection
      attribute="classification"
      className={styles.refinement_list}
      isChipsStyle
      {...props}
    />
  );
};

const GeneralsFilter = ({ ...props }) => (
  <RefinementSection
    attribute="generals"
    className={styles.refinement_list}
    hasSearchBar
    searchBarPlaceholder="Select generals"
    {...props}
  />
);

const EffectsFilter = ({ ...props }) => (
  <RefinementSection
    attribute="effects"
    className={styles.refinement_list}
    hasSearchBar
    searchBarPlaceholder="Select effects"
    {...props}
  />
);

const FlavorsFilter = ({ ...props }) => (
  <RefinementSection
    attribute="flavors"
    className={styles.refinement_list}
    hasSearchBar
    searchBarPlaceholder="Select a flavor"
    {...props}
  />
);

const THCFilter = () => {
  // Treez Docs: The minimum THC % by which to filter.
  const { currentFilterValue, refine, maxNumericValue, hasAttributeValue } =
    useNumericMenu({
      attribute: 'customTHCPercentageMinValue',
      items: [],
    });
  const maxThc = maxNumericValue ? Math.ceil(maxNumericValue) : 100;

  const [range, setRange] = useState<{ start: number; end: number }>({
    start: (currentFilterValue?.gte as number) ?? 0,
    end: (currentFilterValue?.lte as number) ?? maxThc,
  });
  const debouncedRange = useDebounced(range, 500);

  useEffect(() => {
    if (debouncedRange.start !== 0 || debouncedRange.end !== maxThc) {
      refine({ start: debouncedRange.start, end: debouncedRange.end });
      return;
    }
    refine({});
  }, [debouncedRange]);

  useEffect(() => {
    if (
      !currentFilterValue?.gte &&
      !currentFilterValue?.lte &&
      (range.start !== 0 || range.end !== maxThc)
    ) {
      setRange({ start: 0, end: maxThc });
    }
  }, [currentFilterValue]);

  if (!hasAttributeValue) return null;

  return (
    <section className={styles.range_filter}>
      <RangeSlider
        absoluteMax={maxThc}
        absoluteMin={0}
        id="thc-filter"
        maxValue={Math.min(range.end, maxThc)}
        minValue={Math.max(range.start, 0)}
        onChange={(min, max) => setRange({ start: min, end: max })}
        title={filterTitleMapping ? filterTitleMapping.totalThcPercent : 'THC'}
        unit="%"
      />
    </section>
  );
};

const SizeFilter = ({ ...props }) => (
  <RefinementSection
    attribute="customSize"
    className={styles.refinement_list}
    isTwoColumns
    {...props}
  />
);

const TACFilter = () => {
  // Treez Docs: The minimum TAC % by which to filter.
  const { currentFilterValue, refine, maxNumericValue, hasAttributeValue } =
    useNumericMenu({
      attribute: PRODUCT_FILTER_ATTRIBUTES._TOTAL_TAC_PERCENT,
      items: [],
    });
  const maxTac = maxNumericValue ? Math.ceil(maxNumericValue) : 100;

  const [range, setRange] = useState<{ start: number; end: number }>({
    start: (currentFilterValue?.gte as number) ?? 0,
    end: (currentFilterValue?.lte as number) ?? maxTac,
  });
  const debouncedRange = useDebounced(range, 500);

  useEffect(() => {
    if (debouncedRange.start !== 0 || debouncedRange.end !== maxTac) {
      refine({ start: debouncedRange.start, end: debouncedRange.end });
      return;
    }
    refine({});
  }, [debouncedRange]);

  useEffect(() => {
    if (
      !currentFilterValue?.gte &&
      !currentFilterValue?.lte &&
      (range.start !== 0 || range.end !== maxTac)
    ) {
      setRange({ start: 0, end: maxTac });
    }
  }, [currentFilterValue]);

  if (!hasAttributeValue) return null;

  return (
    <section className={styles.range_filter}>
      <RangeSlider
        absoluteMax={maxTac}
        absoluteMin={0}
        id="tac-filter"
        maxValue={Math.min(range.end, maxTac)}
        minValue={Math.max(range.start, 0)}
        onChange={(min, max) => setRange({ start: min, end: max })}
        title={filterTitleMapping ? filterTitleMapping.totalTacPercent : 'TAC'}
        unit="%"
      />
    </section>
  );
};

const CBDFilter = () => {
  // Treez Docs: The minimum CBD % by which to filter.
  const { currentFilterValue, refine, maxNumericValue, hasAttributeValue } =
    useNumericMenu({
      attribute: PRODUCT_FILTER_ATTRIBUTES._TOTAL_CBD_PERCENT,
      items: [],
    });
  const maxCbd = maxNumericValue ? Math.ceil(maxNumericValue) : 100;

  const [range, setRange] = useState<{ start: number; end: number }>({
    start: (currentFilterValue?.gte as number) ?? 0,
    end: (currentFilterValue?.lte as number) ?? maxCbd,
  });
  const debouncedRange = useDebounced(range, 500);

  useEffect(() => {
    if (debouncedRange.start !== 0 || debouncedRange.end !== maxCbd) {
      refine({ start: debouncedRange.start, end: debouncedRange.end });
      return;
    }
    refine({});
  }, [debouncedRange]);

  useEffect(() => {
    if (
      !currentFilterValue?.gte &&
      !currentFilterValue?.lte &&
      (range.start !== 0 || range.end !== maxCbd)
    ) {
      setRange({ start: 0, end: maxCbd });
    }
  }, [currentFilterValue]);

  if (!hasAttributeValue) return null;

  return (
    <section className={styles.range_filter}>
      <RangeSlider
        absoluteMax={maxCbd}
        absoluteMin={0}
        id="cbd-filter"
        maxValue={Math.min(range.end, maxCbd)}
        minValue={Math.max(range.start, 0)}
        onChange={(min, max) => setRange({ start: min, end: max })}
        title={filterTitleMapping ? filterTitleMapping.totalCbdPercent : 'CBD'}
        unit="%"
      />
    </section>
  );
};

const PriceFilter = () => {
  // Treez Docs: The maximum retail price by which to filter.
  const { currentFilterValue, refine, maxNumericValue, hasAttributeValue } =
    useNumericMenu({
      attribute: 'customMinPrice',
      items: [],
    });
  const maxPrice = maxNumericValue ? Math.ceil(maxNumericValue) : 150;

  const [range, setRange] = useState<{ start: number; end: number }>({
    start: (currentFilterValue?.gte as number) ?? 0,
    end: (currentFilterValue?.lte as number) ?? maxPrice,
  });
  const debouncedRange = useDebounced(range, 500);

  useEffect(() => {
    if (debouncedRange.start !== 0 || debouncedRange.end !== maxPrice) {
      refine({ start: debouncedRange.start, end: debouncedRange.end });
      return;
    }
    refine({});
  }, [debouncedRange, maxPrice]);

  useEffect(() => {
    if (
      !currentFilterValue?.gte &&
      !currentFilterValue?.lte &&
      (range.start !== 0 || range.end !== maxPrice)
    ) {
      setRange({ start: 0, end: maxPrice });
    }
  }, [currentFilterValue]);

  if (!hasAttributeValue) return null;

  return (
    <section className={styles.range_filter}>
      <RangeSlider
        absoluteMax={maxPrice}
        absoluteMin={0}
        id="price-filter"
        maxValue={Math.min(range.end, maxPrice)}
        minValue={Math.max(range.start, 0)}
        onChange={(min, max) => setRange({ start: min, end: max })}
        title={filterTitleMapping ? filterTitleMapping.price : 'Price'}
        unit="$"
      />
    </section>
  );
};

const SortByMenu = () => {
  const { activeSort, applySort, items } = useSortingMenu({
    items: sortingOptions,
    attributeMapping: FilterAttributeNameMapping,
  });

  return (
    <div className={styles.sort_by_button}>
      <Select
        aria-label="Sort by"
        className={styles.sort_by_select}
        defaultSelectedLabelClassName={styles.sort_by_default_selected_label}
        menuClassName={styles.sort_by_select_menu}
        menuOptionClassName={styles.sort_by_menu_option}
        onChange={e => applySort(e)}
        options={items}
        placeholderLabel="Sort By"
        placeholderLabelClassName={styles.sort_by_placeholder_label}
        selectLabelClassName={styles.sort_by_select_label}
        value={activeSort}
      />
    </div>
  );
};

const WithFilterAttributeCountLabel = ({
  children,
  attribute,
}: {
  attribute: string;
  // eslint-disable-next-line no-undef
  children: (_: number) => JSX.Element;
}) => {
  const { items } = useRefinementList({ attribute });
  const count = items?.filter(item => item.isRefined)?.length || 0;

  return children(count);
};

const RefinementSection: FC<{
  attribute: string; // this is the attribute use for filter on Treez search product API
  className: string;
  hasSearchBar?: boolean;
  searchBarPlaceholder?: string;
  label?: string;
  isChipsStyle?: boolean;
  isTwoColumns?: boolean;
}> = ({ attribute, className, isChipsStyle, isTwoColumns, ...props }) => {
  const { items } = useRefinementList({ attribute });

  const name = filterAttributeNameMapping?.[attribute] || attribute;
  const label = filterTitleMapping?.[name] ?? startCase(name);

  if (!items?.length) return null;

  return (
    <WithFilterAttributeCountLabel attribute={attribute}>
      {countLabel => (
        <Accordion
          classNameHeader={styles.filters__header_section}
          isExpanded
          label={
            <>
              <span className={styles.filters_list__title}>{label}</span>
              {countLabel > 0 && (
                <span className={styles.filters_list__badge}>{countLabel}</span>
              )}
            </>
          }
        >
          <RefinementList
            attribute={attribute}
            className={className}
            isChipsStyle={isChipsStyle}
            isTwoColumns={isTwoColumns}
            name={label}
            {...props}
          />
        </Accordion>
      )}
    </WithFilterAttributeCountLabel>
  );
};

const filterComponents = {
  brand: BrandFilter,
  category: CategoryFilter,
  classification: ClassificationFilter,
  effect: EffectsFilter,
  flavor: FlavorsFilter,
  general: GeneralsFilter,
  price: PriceFilter,
  deals: DealsFilter,
  subType: SubtypeFilter,
  thc: THCFilter,
  tac: TACFilter,
  cbd: CBDFilter,
  size: SizeFilter,
};

export {
  FiltersContainer,
  FiltersList,
  OutsideFilters,
  FilterButton,
  SortByMenu,
  FiltersModal,
};
